import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import RolesStore from "./RolesStore";

/**
 * Administration → Roles.
 *
 * The page that makes the RBAC model extensible: a business defines the roles
 * it actually has, each declaring the tier it confers, instead of being limited
 * to the seven ERP-shaped roles seeded at install.
 *
 * This is a hand-built view rather than the generic service-view grid on
 * purpose — the reviewed roster in views/utils/ServiceViewRouteGenerator.test.ts
 * keeps that grid for flat reference data and excludes "anything with business
 * rules in hooks or a real blast radius". `roles` is squarely both, and its
 * rules (built-in roles refuse rename, re-tier and delete) need to be *shown*,
 * not just enforced by a 403.
 */

const rolesStoreOf = (rootStore: RootStore) =>
  (rootStore as unknown as { roleAdminStore?: RolesStore }).roleAdminStore;

/**
 * A failed save THROWS, which keeps the dialog open with the API's message
 * (reserved name, refused tier change) in its footer and the form intact.
 * Resolving closes it.
 */
const saveOrThrow = async (rootStore: RootStore) => {
  const store = rolesStoreOf(rootStore);
  if (!(await store?.save())) {
    throw new Error(store?.saveError || "The role could not be saved.");
  }
};

const rolesRoute: RouteType = {
  name: "Roles",
  path: "/roles",
  layout: "/app",
  viewportCard: {
    header: {
      title: "Roles",
      component: () => import("./Roles.Header"),
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./Roles.Body"),
    },
  },
  dialogs: [
    {
      name: "RoleFormDialog",
      title: "Role",
      component: () => import("./dialogs/RoleFormDialog.Body"),
      cancelButton: true,
      height: 480,
      submitButtonText: "Save Role",
      onSubmit: async ({ rootStore }: { rootStore: RootStore }) =>
        saveOrThrow(rootStore),
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
        rolesStoreOf(rootStore)?.isFormValid === true,
    },
  ],
  group: "Admin",
  searchAliases: ["permissions", "rbac", "tier", "access", "custom role"],
  hidden: false,
  requiresAuth: true,
  requiresPermission: false,
  requiresAdmin: true,
  channels: ["roles"],
  enableAutoFind: false,
  onEnter: async ({ rootStore }) => {
    // Through the view store so a failed load is RECORDED (loadError) instead
    // of swallowed by the ServiceStore and rendered as "No roles yet.".
    await rolesStoreOf(rootStore)?.load();
  },
};

export default rolesRoute;
